/**
 * F-JP-20 — the atomic verified write.
 *
 * Twelve steps, and the order of two of them is the whole contract:
 *
 *  - **The backup is verified before the destination is touched.** If the
 *    previous bytes could not be parked somewhere and read back intact, there
 *    is nothing to restore to, so the write does not happen at all and
 *    `writeNotVerified` is thrown with the destination exactly as it was.
 *  - **A rollback that cannot be verified preserves the backup.** It is renamed
 *    into a write-recovery file rather than deleted, and `writeRollbackFailed`
 *    is thrown. At that moment the backup may be the only intact copy of the
 *    record, and tidying it away would convert a recoverable failure into a
 *    permanent loss.
 *
 * There is no "re-apply protection" step here as there is natively. The web
 * substrate has no per-file protection class to re-stamp; the equivalent
 * guarantee — that the bytes are the app's own and reachable by nothing else —
 * comes from the origin-private file system the adapter is built on, and is not
 * something a write can weaken or restore.
 *
 * Everything below reports failure by throwing {@link JournalFileError} rather
 * than returning a union. A twelve-step transaction expressed as nested result
 * matching is a transaction whose branches nobody can check, and the one thing
 * this code must never do is take a wrong branch.
 */

import type { JournalFileStore } from "./files";
import { JournalFileError, journalBytesEqual, journalPathParent } from "./files";
import { journalWriteBackupPath, journalWriteRecoveryPath } from "./layout";

/**
 * Replaces `destination` with `bytes`, or leaves it exactly as it was.
 *
 * `token` distinguishes this write's staging files from any other's. It is
 * supplied rather than generated so the caller owns the only source of
 * non-determinism in the path — a store may not reach for a clock or a random
 * number on its own.
 */
export async function journalVerifiedWrite(
  files: JournalFileStore,
  destination: string,
  bytes: Uint8Array,
  token: string,
): Promise<void> {
  await files.createDirectory(journalPathParent(destination));

  const existing = await files.read(destination);
  if (existing.kind === "failed") {
    // Bytes that cannot be read cannot be backed up, and a record with no
    // backup is a record this write is not allowed to replace.
    throw new JournalFileError(
      "writeNotVerified",
      destination,
      `the existing record could not be read, so it was not overwritten: ${existing.detail}`,
    );
  }

  const previous = existing.kind === "bytes" ? existing.bytes : undefined;
  const backup = journalWriteBackupPath(destination, token);

  if (previous !== undefined) {
    await writeBackupOrRefuse(files, backup, previous, destination);
  }

  const landed = await tryWrite(files, destination, bytes);
  if (landed) {
    // Best effort: the destination is already durable and correct, so a backup
    // that outlives it is litter, not a risk.
    await forget(files, backup);
    return;
  }

  await rollback(files, destination, previous, backup, token);
}

/**
 * Parks the previous bytes and proves they arrived, or refuses.
 *
 * The refusal happens here — before a single byte of the destination has moved
 * — which is what makes "backup verification failure throws before the
 * destination is touched" a property of the code rather than of the caller's
 * discipline.
 */
async function writeBackupOrRefuse(
  files: JournalFileStore,
  backup: string,
  previous: Uint8Array,
  destination: string,
): Promise<void> {
  let verified = false;
  try {
    await files.write(backup, previous);
    const readBack = await files.read(backup);
    verified = readBack.kind === "bytes" && journalBytesEqual(readBack.bytes, previous);
  } catch {
    verified = false;
  }
  if (verified) return;

  // The unverified backup is worthless and confusable with a real one, and the
  // destination still holds the bytes it describes, so removing it loses
  // nothing.
  await forget(files, backup);
  throw new JournalFileError(
    "writeNotVerified",
    destination,
    "the backup of the existing record could not be verified",
  );
}

/** Writes and reads back. Any failure at all is a write that did not land. */
async function tryWrite(
  files: JournalFileStore,
  destination: string,
  bytes: Uint8Array,
): Promise<boolean> {
  try {
    await files.write(destination, bytes);
    const readBack = await files.read(destination);
    return readBack.kind === "bytes" && journalBytesEqual(readBack.bytes, bytes);
  } catch {
    return false;
  }
}

async function rollback(
  files: JournalFileStore,
  destination: string,
  previous: Uint8Array | undefined,
  backup: string,
  token: string,
): Promise<never> {
  if (previous === undefined) {
    // Nothing was there before, so restoring means removing — and the removal
    // is verified exactly as a write is, because a destination that still holds
    // half of a failed write is not "as it was".
    let removed = false;
    try {
      await files.remove(destination);
      removed = (await files.read(destination)).kind === "missing";
    } catch {
      removed = false;
    }
    if (removed) {
      throw new JournalFileError(
        "writeNotVerified",
        destination,
        "the record did not read back as written and the partial file was removed",
      );
    }
    throw new JournalFileError(
      "writeRollbackFailed",
      destination,
      "the record did not read back as written and the partial file could not be removed",
    );
  }

  let restored = false;
  try {
    await files.write(destination, previous);
    const readBack = await files.read(destination);
    restored = readBack.kind === "bytes" && journalBytesEqual(readBack.bytes, previous);
  } catch {
    restored = false;
  }

  if (restored) {
    await forget(files, backup);
    throw new JournalFileError(
      "writeNotVerified",
      destination,
      "the record did not read back as written and the previous bytes were restored",
    );
  }

  // The destination is in an unknown state and the backup may now be the only
  // intact copy. It is renamed, never removed, and the name says what it is so
  // a later recovery pass can find it without decoding anything.
  const recovery = journalWriteRecoveryPath(destination, token);
  try {
    await files.move(backup, recovery);
  } catch {
    // Even the rename failed. The backup stays exactly where it is, which is
    // still a preserved copy — losing the tidy name is not worth losing it.
  }
  throw new JournalFileError(
    "writeRollbackFailed",
    destination,
    "the record could not be restored; the previous bytes are preserved in Staging",
  );
}

/** Removes a staging file, swallowing failure. Never used on a record. */
async function forget(files: JournalFileStore, path: string): Promise<void> {
  try {
    await files.remove(path);
  } catch {
    // A staging file that will not delete is a leak the sweep collects later,
    // and reporting it would fail a write that actually succeeded.
  }
}
